import { useState, useEffect } from 'react';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { useStripe as useStripeHook, useElements } from '@stripe/react-stripe-js';

// Stripe publishable key - should be loaded from environment
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

// Hook to get Stripe instance
export function useStripeInstance() {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    stripePromise
      .then((stripeInstance) => {
        setStripe(stripeInstance);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  return { stripe, isLoading, error };
}

// Hook for Stripe Elements context
export function useStripe() {
  const stripe = useStripeHook();
  const elements = useElements();

  return { stripe, elements };
}

// Hook for creating payment method with Stripe
export function useStripeCreatePaymentMethod() {
  const { stripe, elements } = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPaymentMethod = async (cardElement: any) => {
    if (!stripe || !elements) {
      throw new Error('Stripe has not loaded yet');
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setError(error.message || 'An error occurred');
        return null;
      }

      return paymentMethod;
    } finally {
      setIsLoading(false);
    }
  };

  return { createPaymentMethod, isLoading, error };
}

// Hook for confirming payment with Stripe
export function useStripeConfirmPayment() {
  const { stripe } = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmPayment = async (clientSecret: string, paymentMethodId?: string) => {
    if (!stripe) {
      throw new Error('Stripe has not loaded yet');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await stripe.confirmPayment({
        clientSecret,
        confirmParams: paymentMethodId ? {
          payment_method: paymentMethodId,
        } : undefined,
      });

      if (result.error) {
        setError(result.error.message || 'Payment confirmation failed');
        return { success: false, error: result.error };
      }

      return { success: true, paymentIntent: result.paymentIntent };
    } finally {
      setIsLoading(false);
    }
  };

  return { confirmPayment, isLoading, error };
}

export { stripePromise };